import type { Partner } from "@/types/partner";

export type LocationLevel = "state" | "district" | "taluka" | "village";

const LEVEL_FIELDS: Record<LocationLevel, { idKey: keyof Partner; nameKey: keyof Partner }> = {
  state: { idKey: "state_id", nameKey: "state_name" },
  district: { idKey: "district_id", nameKey: "district_name" },
  taluka: { idKey: "taluka_id", nameKey: "taluka_name" },
  village: { idKey: "village_id", nameKey: "village_name" },
};

export type LocationGroup = {
  id: number | string;
  name: string;
  level: LocationLevel;
  count: number;
  partners: Partner[];
};

// Groups partners by the given level (state/district/taluka/village).
// Callers scope `partners` to the currently selected parent before calling this
// (e.g. only partners within the selected state, when grouping by district).
export function groupByLevel(partners: Partner[], level: LocationLevel): LocationGroup[] {
  const { idKey, nameKey } = LEVEL_FIELDS[level];
  const groups = new Map<string, LocationGroup>();

  for (const partner of partners) {
    const id = partner[idKey] as number | string | null;
    const name = partner[nameKey] as string | null;
    if (id == null || !name) continue;

    const key = String(id);
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.partners.push(partner);
    } else {
      groups.set(key, { id, name, level, count: 1, partners: [partner] });
    }
  }

  return Array.from(groups.values());
}

export type LocationSelection = {
  state?: { id: number | string; name: string };
  district?: { id: number | string; name: string };
  taluka?: { id: number | string; name: string };
};

export function scopeToSelection(partners: Partner[], selection: LocationSelection): Partner[] {
  return partners.filter((p) => {
    if (selection.state && String(p.state_id) !== String(selection.state.id)) return false;
    if (selection.district && String(p.district_id) !== String(selection.district.id)) return false;
    if (selection.taluka && String(p.taluka_id) !== String(selection.taluka.id)) return false;
    return true;
  });
}

// Returns the singular "Partner" label for a count of exactly 1, else the
// plural "Partners" label — both pulled from the given translator so callers
// stay locale-aware.
export function partnersLabel(count: number, t: (key: string) => string): string {
  return count === 1 ? t("type_partner") : t("sub_partners_count");
}

export function filterPartners(partners: Partner[], query: string): Partner[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return partners.filter((p) =>
    [p.name, p.partner_id, p.village_name, p.taluka_name, p.district_name, p.state_name]
      .filter(Boolean)
      .some((field) => field!.toLowerCase().includes(q))
  );
}

// True when every partner in `list` shares the same non-null value for `key`
// as the first one. Used to find how deep a search's results can be scoped
// (state/district/taluka/village) without hiding any matched partner.
export function allShareField<K extends keyof Partner>(list: Partner[], key: K): boolean {
  const first = list[0];
  if (!first) return false;
  const val = first[key];
  if (val == null) return false;
  return list.every((p) => p[key] != null && String(p[key]) === String(val));
}
